import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useAuth } from "@/contexts/AuthContext";
import { useArchives } from "@/hooks/use-archives";
import { useProfileStats, useSubscription } from "@/hooks/useMonProfil";
import { NouvelleSaisineCTA } from "@/components/qv3/NouvelleSaisineCTA";
import { InviteCodeRedeem } from "@/components/qorum/InviteCodeRedeem";
import { CouncilCircle } from "@/components/qv3/CouncilCircle";
import {
  NanaeHeader,
  NanaeFooter,
  QMetric,
  QSectionHeader,
  SaisineCard,
  type SaisineCardData,
  type SaisineStatus,
} from "@/components/qv3";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/foyer")({
  head: () => ({
    meta: [
      { title: "Le Foyer · Hōjin" },
      {
        name: "description",
        content: "Salon avant l'audience du Conseil — saisines, mémos, et accès à l'Antichambre.",
      },
      { name: "robots", content: "noindex" },
      ...socialMeta({
        title: "Le Foyer · Hōjin",
        description: "Salon avant l'audience du Conseil — saisines, mémos, et accès à l'Antichambre.",
        url: "https://hojin.ai/foyer",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/foyer" }],
  }),
  component: () => (
    <ProtectedRoute>
      <FoyerPage />
    </ProtectedRoute>
  ),
});

function FoyerPage() {
  const { user, signOut } = useAuth();
  const archives = useArchives();
  const profileStats = useProfileStats();
  const subscription = useSubscription();
  const plan = subscription.data?.plan ?? "free";
  // Ouvert par défaut : un invité doit voir le champ d'emblée après la 1re
  // connexion (une flèche repliée = champ jamais trouvé → version bridée testée).
  // Repliable après coup via le toggle ↑/↓ ci-dessous — seul l'état initial change.
  const [inviteOpen, setInviteOpen] = useState(true);
  const [inviteDismissed, setInviteDismissed] = useState(false);
  const showInvitePrompt = plan === "free" && !inviteDismissed;
  const quotaUsed = profileStats.data?.quotaUsed ?? null;
  const quotaLimit = profileStats.data?.quotaLimit ?? null;
  const quotaExhausted =
    quotaUsed != null && quotaLimit != null && quotaUsed >= quotaLimit;

  const recent = useMemo<SaisineCardData[]>(() => {
    return (archives.dossiers ?? []).slice(0, 5).map((d) => ({
      id: d.id,
      title: d.question,
      date: d.createdAt ? new Date(d.createdAt).toISOString() : null,
      status: d.status as SaisineStatus,
      mode: typeof d.mode === "string" ? d.mode : null,
    }));
  }, [archives.dossiers]);

  const stats = useMemo(() => {
    const total = archives.totalCount ?? archives.dossiers.length;
    const memos = archives.dossiers.filter((d) => d.status === "completed").length;
    return { total, memos };
  }, [archives.dossiers, archives.totalCount]);

  // Cercle du Conseil : taille adaptée à la largeur réellement disponible
  // (évite l'overflow horizontal sur smartphone, conserve 460 px desktop).
  const circleWrapRef = useRef<HTMLDivElement>(null);
  const [circleSize, setCircleSize] = useState(460);
  useEffect(() => {
    const measure = () => {
      const w = circleWrapRef.current?.clientWidth ?? 460;
      setCircleSize(Math.max(280, Math.min(460, Math.floor(w))));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div className="nanae-page qv3-dark">
      <NanaeHeader
        title="Le Foyer"
        user={{
          email: user?.email,
          name:
            (user?.user_metadata as { first_name?: string } | undefined)?.first_name ??
            user?.email,
        }}
        rightSlot={
          <button
            type="button"
            onClick={() => void signOut()}
            className="qv3-btn-link"
            style={{ color: "inherit" }}
          >
            Déconnexion
          </button>
        }
      />

      <main className="nanae-page-main">
        {/* § I — Nouvelle saisine + Le Conseil (cercle à droite) */}
        <section style={{ marginBottom: 64 }}>
          <QSectionHeader number="I" title="NOUVELLE SAISINE" />
          <div className="nanae-foyer-saisine-row">
            <div style={{ display: "flex", flexDirection: "column", gap: 24, marginTop: 24, minWidth: 0 }}>
              <NouvelleSaisineCTA quotaExhausted={quotaExhausted} />
              <div className="nanae-stats-row">
                <QMetric label="SAISINES" value={stats.total} size="lg" />
                <QMetric label="MÉMOS RENDUS" value={stats.memos} size="lg" />
                <QMetric label="CADRES" value="7" size="lg" />
              </div>
            </div>
            <div
              ref={circleWrapRef}
              style={{ marginTop: -48, display: "flex", alignItems: "flex-start", justifyContent: "center", width: "100%", minWidth: 0 }}
            >
              <CouncilCircle size={circleSize} />
            </div>
          </div>
        </section>

        {showInvitePrompt && (
          <section style={{ marginBottom: 48 }}>
            <div
              style={{
                border: "0.5px solid var(--qv3-grey-200, #ccc)",
                background: "var(--qv3-paper, #fff)",
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setInviteOpen((v) => !v)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontFamily: "var(--qv3-font-serif)",
                    fontStyle: "italic",
                    fontSize: 16,
                    color: "var(--qv3-ink, #000)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  aria-expanded={inviteOpen}
                  aria-controls="invite-prompt-panel"
                >
                  Vous avez un code d'invitation ? {inviteOpen ? "↑" : "↓"}
                </button>
                <button
                  type="button"
                  onClick={() => setInviteDismissed(true)}
                  aria-label="Masquer l'invitation"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 4,
                    fontFamily: "var(--qv3-font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.2em",
                    color: "var(--qv3-grey-500)",
                    cursor: "pointer",
                  }}
                >
                  Masquer
                </button>
              </div>
              {inviteOpen && (
                <div id="invite-prompt-panel">
                  <InviteCodeRedeem
                    variant="inline"
                    onSuccess={() => setInviteDismissed(true)}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {/* § II — Dernières saisines */}
        <section style={{ marginBottom: 64 }}>
          <QSectionHeader
            number="II"
            title="DERNIÈRES SAISINES"
            hint={archives.loading ? "Chargement…" : undefined}
          />
          <div style={{ marginTop: 16 }}>
            {recent.length === 0 && !archives.loading && (
              <p
                style={{
                  fontFamily: "var(--qv3-font-serif)",
                  fontStyle: "italic",
                  color: "var(--qv3-grey-500)",
                  padding: "24px 0",
                  margin: 0,
                }}
              >
                Aucune saisine encore. Présentez la première au Conseil.
              </p>
            )}
            {recent.map((s) => (
              <SaisineCard key={s.id} saisine={s} />
            ))}
            {(archives.totalCount ?? 0) > recent.length && (
              <div style={{ paddingTop: 24, textAlign: "right" }}>
                <Link to="/archives" className="qv3-btn-link" style={{ color: "inherit" }}>
                  Toutes les saisines →
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <NanaeFooter variant="minimal" />
    </div>
  );
}
